import { ButtonLink } from "@/components/links";
import { StatePage } from "@/components/state-page";
import { filterKeys } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { CONSTANTS } from "@/utils/constants";
import { requirePermissions, useHasPermission } from "@/utils/permissions";
import type { APIOutput } from "@/utils/types";
import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import type { AdminTrip } from "@workspace/admin-domain/src/features/trip/schema";
import { t } from "i18next";
import { LucideDownload, LucidePlus } from "lucide-react";
import TripsControlBar from "./-control-bar";
import TripsList from "./-list";
import { DEFAULT_TRIPS_PARAMS, TripFiltersSchema } from "./-utils";

export const Route = createFileRoute("/_authenticated/trips/")({
  validateSearch: TripFiltersSchema,
  component: Component,
  beforeLoad: requirePermissions("trip:get"),
  loaderDeps: ({ search }) => filterKeys(search, TripFiltersSchema.shape),
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchInfiniteQuery(
      orpc.trip.getAll.infiniteOptions({
        input: (paginate) => ({
          ...DEFAULT_TRIPS_PARAMS,
          ...deps,
          paginate,
        }),
        initialPageParam: undefined as AdminTrip["GetAll"]["paginate"],
        getNextPageParam: () => undefined as AdminTrip["GetAll"]["paginate"],
      }),
    );
  },
  errorComponent: ({ error, reset }) => (
    <StatePage error={error} reset={reset} resourceType="trips" emptyResourceType="trips.trip" />
  ),
  notFoundComponent: () => <StatePage isNotFound resourceType="trips" emptyResourceType="trips.trip" />,
});

function Component() {
  const search = useSearch({ from: "/_authenticated/trips/" });
  const queryClient = useQueryClient();
  const filters = filterKeys(search, TripFiltersSchema.shape);
  const hasTripCreatePermission = useHasPermission("trip:create");

  const exportAllMutation = useMutation({
    mutationFn: async () => {
      const [{ pdf, Document }, { TripPDFPage }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("./-pdf-document"),
      ]);

      const agency = await queryClient.fetchQuery(orpc.agency.get.queryOptions());

      // Fetch all trips with pagination
      const allTrips: APIOutput["trip"]["getAll"] = [];
      let paginate: AdminTrip["GetAll"]["paginate"] | undefined = undefined;
      let hasMore = true;
      const limit = 1000;

      while (hasMore) {
        const trips: APIOutput["trip"]["getAll"] = await queryClient.fetchQuery(
          orpc.trip.getAll.queryOptions({
            input: {
              ...DEFAULT_TRIPS_PARAMS,
              ...filters,
              limit,
              paginate,
              sortBy: filters.sortBy ?? DEFAULT_TRIPS_PARAMS.sortBy,
              sortOrder: filters.sortOrder ?? DEFAULT_TRIPS_PARAMS.sortOrder,
            },
          }),
        );

        allTrips.push(...trips);

        if (trips.length < limit) {
          hasMore = false;
        } else {
          const lastItem = trips[trips.length - 1];
          if (lastItem) {
            paginate = {
              id: lastItem.id,
              cursor: lastItem[filters.sortBy ?? "createdAt"],
            };
          } else {
            hasMore = false;
          }
        }
      }

      if (allTrips.length === 0) {
        throw new Error(t("trips.exportPDF.all.empty"));
      }

      // Fetch reservations for each trip and prepare PDF pages
      const tripPages = await Promise.all(
        allTrips.map(async (trip) => {
          const reservations = await queryClient.fetchQuery(
            orpc.reservation.getAll.queryOptions({
              input: {
                value: trip.id,
                type: "tripId",
              },
            }),
          );

          return (
            <TripPDFPage
              key={trip.id}
              trip={trip}
              agency={agency}
              reservations={reservations}
              baseUrl={window.location.origin}
            />
          );
        }),
      );

      const doc = <Document>{tripPages}</Document>;

      const blob = await pdf(doc).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().split("T")[0];
      link.download = `trips-${dateStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      notifications.show({
        title: t("success"),
        message: t("trips.exportPDF.all.success.description"),
        color: "green",
      });
    },
    onError: (error) => {
      notifications.show({
        title: t("error.title"),
        message: error instanceof Error ? error.message : t("trips.exportPDF.all.error.description"),
        color: "red",
      });
    },
  });

  return (
    <div className="gap-md md:gap-lg flex grow flex-col">
      <div className="py-md flex flex-wrap items-center justify-between gap-x-6 gap-y-4 md:justify-start">
        <p className="text-2xl font-semibold md:text-3xl">{t("trips.yourTrips")}</p>
        <div className="grid grow grid-cols-2 items-center gap-1.5 md:flex">
          <Button
            variant="default"
            leftSection={<LucideDownload className="size-4" />}
            onClick={() => exportAllMutation.mutate()}
            disabled={CONSTANTS.DEMO}
            loading={exportAllMutation.isPending}
          >
            {t("export")}
          </Button>
          {hasTripCreatePermission ? (
            <ButtonLink to="/trips/new" leftSection={<LucidePlus />}>
              {t("trips.new")}
            </ButtonLink>
          ) : null}
        </div>
      </div>
      <TripsControlBar />
      <TripsList />
    </div>
  );
}
